import {v4 as uuidv4} from 'uuid';
import {htmlToDom} from '../services/dom';
import {debounce} from '../services/util';
import {KeyboardNavigationHandler} from '../services/keyboard-navigation';
import {Component} from './component';
import SearchAiModal from './global-search-modal';

const badgeIcon = '<svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" class="mr-0.5 my-auto" height="12" width="12" xmlns="http://www.w3.org/2000/svg"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>';

const TEMP_STRING = '__$%^TEMP$%^__';

function formatResponse(answer) {
    answer = answer.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    answer = answer.replace(/```([^`]+)```/gs, '<pre><code>$1</code></pre>');
    answer = answer.replace(/`([^`]+)`/gs, '<code>$1</code>');
    answer = answer.replace(/\\\\n/g, TEMP_STRING);
    answer = answer.replace(/\\n/g, '\n');
    answer = answer.replace(TEMP_STRING, '\\n');
    answer = answer.replace(/\n/g, '<br>');
    return answer;
}

const processSingleChunk = (chunk, currPartialChunk) => {
    const completeChunk = (currPartialChunk || '') + chunk;
    try {
        // every complete chunk should be valid JSON
        const chunkJson = JSON.parse(completeChunk);
        return [chunkJson, null];
    } catch (err) {
        // if it's not valid JSON, then it's probably an incomplete chunk
        return [null, completeChunk];
    }
};

const endsWithLetterOrNumber = str => {
    return /[a-zA-Z0-9]$/.test(str);
};

const removeDuplicateDocs = documents => {
    const seen = new Set();
    const output = [];
    documents.forEach(document => {
        if (document.document_id && !seen.has(document.document_id)) {
            output.push(document);
            seen.add(document.document_id);
        }
    });
    return output;
};

const processRawChunkString = (rawChunkString, previousPartialChunk) => {
    /* This is required because, in practice, we see that nginx does not send over
  each chunk one at a time even with buffering turned off. Instead,
  chunks are sometimes in batches or are sometimes incomplete */
    if (!rawChunkString) {
        return [[], null];
    }
    const chunkSections = rawChunkString
        .split('\n')
        .filter(chunk => chunk.length > 0);
    const parsedChunkSections = [];
    let currPartialChunk = previousPartialChunk;
    chunkSections.forEach(chunk => {
        const [processedChunk, partialChunk] = processSingleChunk(
            chunk,
            currPartialChunk,
        );
        if (processedChunk) {
            parsedChunkSections.push(processedChunk);
            currPartialChunk = null;
        } else {
            currPartialChunk = partialChunk;
        }
    });

    return [parsedChunkSections, currPartialChunk];
};

function timeAgo(dateString) {
    try {
        const now = new Date();
        const pastDate = new Date(dateString);
        const diffInSeconds = Math.floor((now - pastDate) / 1000);

        const timeUnits = [
            {unit: 'year', value: 31536000},
            {unit: 'month', value: 2592000},
            {unit: 'day', value: 86400},
            {unit: 'hour', value: 3600},
            {unit: 'minute', value: 60},
            {unit: 'second', value: 1},
        ];

        const result = timeUnits.find(({value}) => diffInSeconds / value >= 1);

        const interval = Math.floor(diffInSeconds / result.value);
        return `${interval} ${result.unit}${interval > 1 ? 's' : ''} ago`;
    } catch {
        // should never happen
        return 'some time ago';
    }
}

const aiSearch = new Event('ai-search');
const aiSearchDone = new Event('ai-search-done');

export class GlobalSearch extends Component {

    async setup() {
        this.container = this.$el;
        this.form = this.$refs.form;
        this.input = this.$refs.input;
        this.suggestions = this.$refs.suggestions;
        this.suggestionResults = this.$refs.suggestionResults;
        this.loadingWrap = this.$refs.loading;
        this.button = this.$refs.button;
        this.viewAllBtn = this.$refs.viewAllBtn;
        this.suggestionResultsWrapper = this.$refs.suggestionResultsWrapper;
        this.aiAnswerWrapper = this.$refs.aiAnswerWrapper;
        this.aiAnswerContent = this.$refs.aiAnswerContent;
        this.isCvApiKeySet = null;
        this.indexingStatus = null;
        this.statusError = '';
        this.aiSearchResults = this.$refs.aiSearchResults;
        this.aiSearchHeader = this.$refs.aiSearchHeader;
        this.generatedAnswerHeader = this.$refs.generatedAnswerHeader;
        this.searchWithAiBtn = this.$refs.searchWithAiBtn;
        this.notAnswerableNote = this.$refs.notAnswerableNote;
        this.forceAnswerBtn = this.$refs.forceAnswerBtn;
        this.aiAnswerHint = this.$refs.aiAnswerHint;

        this.reader = null;

        this.setupListeners();
        this.setupSearchAiModal();
        await this.setupApiKeyCheck();
        await this.setupIndexingStatusCheck();

        if (this.isCvApiKeySet && this.indexingStatus) {
            this.searchWithAiBtn.removeAttribute('disabled');
            this.aiAnswerHint.classList.remove('v-hidden');
        } else {
            this.showStatusError();
        }
    }

    setupListeners() {
        const updateSuggestionsDebounced = debounce(
            this.updateSuggestions.bind(this),
            200,
            false,
        );

        // Handle search input changes
        this.input.addEventListener('input', () => {
            const {value} = this.input;
            updateSuggestionsDebounced(value);
            if (value.length > 0) {
                this.suggestionResultsWrapper.classList.remove('d-none');
                this.loadingWrap.classList.remove('d-none');
                this.aiAnswerWrapper.classList.remove('d-none');
                this.searchWithAiBtn.innerText = 'Search with AI';
            } else {
                this.container.classList.remove('answerable');
                this.suggestionResultsWrapper.classList.add('d-none');
                this.aiAnswerWrapper.classList.add('d-none');
                this.stopStreamResponse();
                this.hideSuggestions();
                this.hideAiSearchResults();
                this.aiAnswerContent.innerHTML = '';
                this.aiAnswerContent.classList.add('d-none');
                this.generatedAnswerHeader.classList.add('d-none');
            }
        });

        this.input.addEventListener('keydown', event => {
            if (event.key === 'Enter') {
                event.preventDefault();
            }
        });

        this.searchWithAiBtn.addEventListener('click', async () => {
            if (!this.isCvApiKeySet || !this.indexingStatus) { 
                return
            }
            this.aiAnswerWrapper.classList.remove('loading');
            if (this.reader) {
                await this.stopStreamResponse();
                this.notAnswerableNote.classList.add('d-none');
            } else {
                this.searchWithAiBtn.innerText = 'Stop generating response';
                await this.updateAiSearchResults(this.input.value);
            }
            this.searchWithAiBtn.innerText = 'Search with AI';
        });

        this.viewAllBtn.addEventListener('click', () => {
            this.form.submit();
        });

        this.forceAnswerBtn.addEventListener('click', () => {
            this.container.classList.add('answerable');
            this.notAnswerableNote.classList.add('d-none');
        });

        // Allow double click to show auto-click suggestions
        this.input.addEventListener('dblclick', () => {
            this.input.setAttribute('autocomplete', 'on');
            this.button.focus();
            this.input.focus();
        });

        new KeyboardNavigationHandler(this.container, () => {
            this.hideSuggestions();
        });
    }

    async setupApiKeyCheck() {
        const response = await fetch(
            `${window.location.origin}/verify-cv-api-key`,
        );
        const responseBody = await response.json();
        if (responseBody.success) {
            this.isCvApiKeySet = true;
            return;
        }
        this.statusError = 'Contact your CogniVis administrator to enable this feature.';
        this.isCvApiKeySet = false;
    }

    async setupIndexingStatusCheck() {
        if (!this.isCvApiKeySet) {
            return;
        }

        const response = await fetch(
            `${window.location.origin}/indexing-status`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );

        const responseBody = await response.json();
        responseBody.data.forEach(el => {
            if (el.source === 'bookstack') {
                this.indexingStatus = el.has_successful_run;
            }
        });

        if (!this.indexingStatus) {
            this.statusError = '<b>Note:</b> You need to add CogniVis Docs as a connector in your CogniVis AI istance to enable this feature.';
        }
    }

    /**
     * @param {String} search
     */
    // eslint-disable-next-line
    async updateSuggestions(search) {
        this.suggestionResultsWrapper.classList.add('loading');
        const {data: results} = await window.$http.get('/search/suggest', {
            term: search,
        });
        this.suggestionResultsWrapper.classList.remove('loading');
        if (this.input.value) {
            this.showSuggestions();
        } else {
            return this.hideSuggestions();
        }
        const resultDom = htmlToDom(results);

        this.suggestionResults.innerHTML = '';
        this.loadingWrap.classList.add('d-none');
        this.suggestionResults.append(resultDom);
    }

    async updateAiSearchResults(search) {
        if (!this.isCvApiKeySet || !this.indexingStatus) {
            return;
        }

        const reportingSessionId = uuidv4();

        this.aiAnswerContent.classList.remove('d-none');
        this.searchWithAiBtn.setAttribute('disabled', 'disabled');
        this.notAnswerableNote.classList.add('d-none');

        this.questionValidationStreamed(search, reportingSessionId);
        document.dispatchEvent(aiSearch);

        const searchAiReqBody = {
            messages: [{message: search, sender: null, role: 'user'}],
            persona_id: 0,
            prompt_id: null,
            retrieval_options: {
                run_search: 'always',
                real_time: true,
                filters: {
                    source_type: null,
                    document_set: null,
                    time_cutoff: null,
                    tags: [],
                },
                enable_auto_detect_filters: false,
            },
            metadata: {
                reporting_flow: 'cv_docs_search_with_ai_message',
                reporting_session_id: reportingSessionId,
            },
        };

        this.aiAnswerWrapper.classList.add('loading');
        this.aiAnswerContent.innerHTML = '';

        try {
            const aiResponse = await fetch(
                `${window.location.origin}/ai/search-ai`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': `${document
                            .querySelector('meta[name="token"]')
                            .getAttribute('content')}`,
                    },
                    body: JSON.stringify(searchAiReqBody),
                },
            );

            this.reader = aiResponse.body?.getReader();
            const decoder = new TextDecoder('utf-8');

            let previousPartialChunk = null;
            let aiTextResponse = '';
            this.hideAiSearchResults();

            while (true) {
                this.searchWithAiBtn.removeAttribute('disabled');
                // eslint-disable-next-line no-await-in-loop
                const rawChunk = await this.reader?.read();
                if (!rawChunk) {
                    throw new Error('Unable to process chunk');
                }
                const {done, value} = rawChunk;
                if (done) {
                    document.dispatchEvent(aiSearchDone);
                    this.reader = null;
                    this.searchWithAiBtn.innerText = 'Regenerate response';

                    if (!this.input.value) {
                        this.aiAnswerContent.innerHTML = '';
                        this.hideAiSearchResults();
                    }
                    break;
                }

                const [completedChunks, partialChunk] = processRawChunkString(
                    decoder.decode(value, {stream: true}),
                    previousPartialChunk,
                );
                previousPartialChunk = partialChunk;
                // eslint-disable-next-line no-loop-func
                completedChunks.forEach(chunk => {
                    if (Object.hasOwn(chunk, 'answer_piece')) {
                        this.aiAnswerWrapper.classList.remove('loading');
                        this.generatedAnswerHeader.classList.remove('d-none');
                        const answerPiece = chunk.answer_piece;
                        if (answerPiece !== null) {
                            aiTextResponse += answerPiece;
                            this.aiAnswerContent.innerHTML = formatResponse(aiTextResponse);
                        } else {
                            // set quotes as non-null to signify that the answer is finished and
                            // this.updateQuotes([]);

                            // eslint-disable-next-line no-lonely-if
                            if (
                                aiTextResponse
                                && !aiTextResponse.endsWith('.')
                                && !aiTextResponse.endsWith('?')
                                && !aiTextResponse.endsWith('!')
                                && endsWithLetterOrNumber(aiTextResponse)
                            ) {
                                aiTextResponse += '.';
                                this.aiAnswerContent.innerHTML = formatResponse(aiTextResponse);
                            }
                        }
                        return;
                    }

                    if (Object.hasOwn(chunk, 'error')) {
                        console.error(chunk.error);
                        return;
                    }

                    if (Object.hasOwn(chunk, 'top_documents')) {
                        const topDocuments = removeDuplicateDocs(chunk.top_documents);
                        if (topDocuments) {
                            this.addAiSearchResults(topDocuments, 5);
                        }
                    }
                });
            }
        } catch (err) {
            console.error('Fetch error:', err);
        }
    }

    addAiSearchResults(documents, limit) {
        if (!documents || documents.length === 0) {
            return;
        }
        const documentsLimited = documents.slice(0, limit);
        this.aiSearchResults.innerHTML = '';
        this.showAiSearchResults();

        documentsLimited.forEach(doc => {
            if (doc.score < 0.35) {
                return;
            }
            const docWrapper = document.createElement('div');
            docWrapper.classList.add('ai-search-related-document');

            const docHead = document.createElement('div');
            docHead.classList.add('position-relative');

            const docScoreBadge = document.createElement('div');
            docScoreBadge.classList.add('ai-search-doc-badge');
            docScoreBadge.classList.add('score-badge');
            docScoreBadge.innerHTML = `${doc.score.toFixed(2)}`;

            const docLink = document.createElement('a');
            docLink.setAttribute('href', doc.link);
            docLink.setAttribute('target', '_blank');
            docLink.setAttribute('rel', 'noopener noreferrer');
            docLink.innerHTML = doc.semantic_identifier;

            const docBadgesWrapper = document.createElement('div');
            docBadgesWrapper.classList.add('ai-search-result-badges');
            docBadgesWrapper.classList.add('my-auto');

            const docUpdateBadge = document.createElement('div');
            docUpdateBadge.classList.add('ai-search-doc-badge');
            docUpdateBadge.innerHTML = `updated=${timeAgo(doc.updated_at)}`;

            const docTypeBadge = document.createElement('div');
            docTypeBadge.classList.add('ai-search-doc-badge');
            docTypeBadge.innerHTML = `${badgeIcon} type=${doc.metadata.type}`;

            docBadgesWrapper.append(docUpdateBadge);
            docBadgesWrapper.append(docTypeBadge);

            const docDesc = document.createElement('p');
            docDesc.innerHTML = formatResponse(doc.blurb);

            docHead.append(docLink);
            docHead.append(docScoreBadge);

            docWrapper.append(docHead);
            docWrapper.append(docBadgesWrapper);
            docWrapper.append(docDesc);
            this.aiSearchResults.append(docWrapper);
        });
    }

    questionValidationStreamed = async (query, reportingSessiondId) => {
        this.container.classList.remove('answerable');

        const response = await fetch(
            `${window.location.origin}/ai/stream-query-validation`,
            {
                method: 'POST',
                body: JSON.stringify({
                    query,
                    metadata: {
                        reporting_flow: 'cv_docs_search_with_ai_message',
                        reporting_session_id: reportingSessiondId,
                    },
                }),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': `${document
                        .querySelector('meta[name="token"]')
                        .getAttribute('content')}`,
                },
            },
        );
        const reader = response.body?.getReader();
        const decoder = new TextDecoder('utf-8');

        let previousPartialChunk = null;
        while (true) {
            // eslint-disable-next-line no-await-in-loop
            const rawChunk = await reader?.read();
            if (!rawChunk) {
                throw new Error('Unable to process chunk');
            }
            const {done, value} = rawChunk;
            if (done) {
                break;
            }

            const [completedChunks, partialChunk] = processRawChunkString(
                decoder.decode(value, {stream: true}),
                previousPartialChunk,
            );
            if (!completedChunks.length && !partialChunk) {
                break;
            }
            previousPartialChunk = partialChunk;

            completedChunks.forEach(chunk => {
                if (Object.hasOwn(chunk, 'answerable')) {
                    if (chunk.answerable) {
                        this.container.classList.add('answerable');
                    } else {
                        this.notAnswerableNote.classList.remove('d-none');
                    }
                }

                if (Object.hasOwn(chunk, 'error')) {
                    console.error(chunk.error);
                }
            });
        }
    };

    showStatusError() {
        this.aiAnswerContent.innerHTML = `${this.statusError}`;
        this.aiAnswerContent.classList.add('invalid-feedback');
    }

    showSuggestions() {
        this.container.classList.add('search-active');
        this.viewAllBtn.classList.remove('d-none');
    }

    hideSuggestions() {
        this.container.classList.remove('search-active');
        this.suggestionResults.innerHTML = '';
        this.viewAllBtn.classList.add('d-none');
    }

    hideAiSearchResults() {
        this.aiSearchResults.innerHTML = '';
        this.aiSearchResults.classList.add('d-none');
        this.aiSearchHeader.classList.add('d-none');
    }

    showAiSearchResults() {
        this.aiSearchResults.classList.remove('d-none');
        this.aiSearchHeader.classList.remove('d-none');
    }

    stopStreamResponse = async () => {
        if (this.reader) {
            await this.reader.cancel();
            this.reader = null;
        }
    };

    setupSearchAiModal() {
        this.SearchAiModal = new SearchAiModal();
        const openButton = document.querySelector(
            '.search-box .search-modal-open-btn',
        );
        const modal = document.querySelector('.search-modal');
        this.SearchAiModal.init(openButton, modal, this.input);
    }

}
